import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Download,
  Eye,
  EyeOff,
  Flag,
  Heart,
  Play,
  Trash2,
  X,
} from 'lucide-react';
import { api } from '../lib/api';

type StoryItem = {
  id: string;
  uploader_name: string;
  media_url: string;
  thumb_url: string | null;
  media_type: 'image' | 'video';
  caption: string | null;
  created_at: string;
  expires_at: string;
  is_hidden: boolean;
  likes: number;
  views: number;
  reports: number;
};

/** גלריית האירוע לזוג — כל הסטוריז והמדיה, כולל מודרציה (הסתרה / מחיקה) */
export default function EventGalleryTab({ eventId }: { eventId: string }) {
  const [items, setItems] = useState<StoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyReported, setOnlyReported] = useState(false);
  const [preview, setPreview] = useState<StoryItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/events/${eventId}/stories`)
      .then((r) => setItems(r.data ?? []))
      .catch(() => toast.error('שגיאה בטעינת הגלריה'))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHidden = async (item: StoryItem) => {
    try {
      await api.patch(`/events/${eventId}/stories/${item.id}`, { is_hidden: !item.is_hidden });
      setItems((prev) =>
        prev.map((s) => (s.id === item.id ? { ...s, is_hidden: !item.is_hidden } : s)),
      );
      toast.success(item.is_hidden ? 'הפריט הוחזר לגלריה' : 'הפריט הוסתר מהאורחים');
    } catch {
      toast.error('הפעולה נכשלה');
    }
  };

  const remove = async (item: StoryItem) => {
    if (!window.confirm(`למחוק לצמיתות את המדיה של ${item.uploader_name}? אי אפשר לשחזר.`)) return;
    try {
      await api.delete(`/events/${eventId}/stories/${item.id}`);
      setItems((prev) => prev.filter((s) => s.id !== item.id));
      setPreview(null);
      toast.success('נמחק');
    } catch {
      toast.error('המחיקה נכשלה');
    }
  };

  const shown = onlyReported ? items.filter((s) => s.reports > 0) : items;
  const reportedCount = items.filter((s) => s.reports > 0).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-ink">גלריית האירוע</h2>
          <p className="text-sm text-ink-mute">
            כל הסטוריז והתמונות שהאורחים העלו — {items.length} פריטים
          </p>
        </div>
        {reportedCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyReported((v) => !v)}
            className={onlyReported ? 'btn-primary !py-2 !px-4 text-sm' : 'btn-secondary !py-2 !px-4 text-sm'}
          >
            <Flag size={14} className="inline ml-1.5" />
            {onlyReported ? 'הצג הכול' : `דיווחים (${reportedCount})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-2xl bg-paper-mist animate-pulse" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="surface-quiet rounded-3xl p-12 text-center">
          <div className="icon-tile mx-auto mb-4">
            <Eye size={22} className="text-sage-600" />
          </div>
          <p className="font-semibold text-ink">
            {onlyReported ? 'אין פריטים מדווחים' : 'עדיין אין מדיה באירוע'}
          </p>
          <p className="text-sm text-ink-mute mt-1">
            {onlyReported
              ? 'כל הדיווחים טופלו'
              : 'ברגע שאורחים יעלו סטוריז מהאפליקציה — הכול יופיע כאן'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {shown.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setPreview(item)}
              className={`group relative aspect-square rounded-2xl overflow-hidden bg-paper-mist text-right transition-transform duration-200 hover:scale-[1.02] cursor-pointer ${
                item.is_hidden ? 'opacity-50' : ''
              }`}
            >
              <img
                src={item.thumb_url || item.media_url}
                alt={item.caption || item.uploader_name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {item.media_type === 'video' && (
                <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/55 flex items-center justify-center">
                  <Play size={12} className="text-white" fill="white" />
                </span>
              )}
              {item.reports > 0 && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-red-500 text-white text-[11px] font-bold px-2 py-0.5">
                  <Flag size={10} /> {item.reports}
                </span>
              )}
              {item.is_hidden && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <EyeOff size={22} className="text-white" />
                </span>
              )}
              <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/65 to-transparent px-2.5 pb-2 pt-6 text-white">
                <span className="block text-[12px] font-semibold truncate">{item.uploader_name}</span>
                <span className="flex items-center gap-2 text-[11px] opacity-90">
                  <span className="inline-flex items-center gap-0.5"><Heart size={10} /> {item.likes}</span>
                  <span className="inline-flex items-center gap-0.5"><Eye size={10} /> {item.views}</span>
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="relative max-w-3xl w-full max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between text-white mb-3">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="p-1.5 rounded-full hover:bg-white/15 transition-colors cursor-pointer"
              >
                <X size={22} />
              </button>
              <div className="text-left">
                <p className="font-bold">{preview.uploader_name}</p>
                <p className="text-xs opacity-80">
                  {new Date(preview.created_at).toLocaleString('he-IL')}
                  {preview.reports > 0 && ` · ${preview.reports} דיווחים`}
                </p>
              </div>
            </div>

            <div className="flex-1 min-h-0 rounded-2xl overflow-hidden bg-black flex items-center justify-center">
              {preview.media_type === 'video' ? (
                <video src={preview.media_url} controls autoPlay className="max-h-[62vh] w-full" />
              ) : (
                <img
                  src={preview.media_url}
                  alt={preview.caption || ''}
                  className="max-h-[62vh] w-auto mx-auto object-contain"
                />
              )}
            </div>

            {preview.caption && (
              <p className="text-white/90 text-sm text-center mt-3">{preview.caption}</p>
            )}

            <div className="flex items-center justify-center gap-3 mt-4">
              <a
                href={preview.media_url}
                download
                target="_blank"
                rel="noreferrer"
                className="btn-secondary !py-2 !px-4 text-sm !bg-white"
              >
                <Download size={14} className="inline ml-1.5" /> הורדה
              </a>
              <button
                type="button"
                onClick={() => {
                  void toggleHidden(preview);
                  setPreview((p) => (p ? { ...p, is_hidden: !p.is_hidden } : p));
                }}
                className="btn-secondary !py-2 !px-4 text-sm !bg-white"
              >
                {preview.is_hidden ? (
                  <>
                    <Eye size={14} className="inline ml-1.5" /> החזרה לגלריה
                  </>
                ) : (
                  <>
                    <EyeOff size={14} className="inline ml-1.5" /> הסתרה מהאורחים
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => void remove(preview)}
                className="btn-secondary !py-2 !px-4 text-sm !bg-red-50 !text-red-600 !border-red-200"
              >
                <Trash2 size={14} className="inline ml-1.5" /> מחיקה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
